/**
 * One Data Room document: rendered Markdown, its identity line — category,
 * current version, who touched it last and when — and the verbs: edit,
 * version history, delete, and, for a deleted document, restore.
 *
 * Versioning rules the page renders around:
 *
 *   * Saving an edit creates the next revision; the page always reloads to
 *     the server's answer rather than trusting its own optimism.
 *   * A stale save is a conflict, said plainly, with nothing silently
 *     overwritten and nothing typed thrown away until the author decides.
 *   * Restoring copies an old revision forward as a new one — the history
 *     only ever grows, and the dialog says exactly that before confirming.
 *
 * The version history lives in a drawer — a focused panel the "Version
 * history" button beside the title opens, a side sheet on desktop and a
 * bottom sheet on phones — not a table permanently stapled under the
 * document. Reading an old revision happens in place: choosing Preview
 * closes the drawer and swaps the document body, with a banner naming the
 * version being read and the way back to the current one.
 *
 * Deletion is soft and honest about it: the document leaves every ordinary
 * surface but keeps its page, which says who deleted it and offers restore.
 * Nothing on a deleted document can change except that one act.
 */

import { useCallback, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  DATA_ROOM_MAX_CONTENT_LENGTH,
  DATA_ROOM_MAX_TITLE_LENGTH,
} from '@mouja/domain/data-room';

import {
  ApiFailure,
  deleteDataRoomDocument,
  editDataRoomDocument,
  fetchDataRoomDocument,
  fetchDataRoomHistory,
  fetchDataRoomRevision,
  newIdempotencyKey,
  recoverDataRoomDocument,
  restoreDataRoomRevision,
  type DataRoomRevisionDetail,
  type DataRoomRevisionMeta,
} from '../api.js';
import {
  EventTime,
  MarkdownField,
  MarkdownView,
  useWorkspaceCopy,
} from '../components.js';
import { useSession } from '../session.js';
import { workspaceSlots } from '../slots.js';
import {
  Button,
  ConfirmDialog,
  Drawer,
  InlineAlert,
  Tag,
  TextField,
  useToasts,
} from '../ui.js';
import { FailurePanel, LoadingPanel, useAsync } from './common.js';
import { NotAvailablePage } from './NotAvailablePage.js';

export function DataRoomDocumentPage(): ReactNode {
  const copy = useWorkspaceCopy();
  const { session } = useSession();
  const { announce } = useToasts();
  const { id } = useParams<{ id: string }>();
  const documentId = id ?? '';

  const load = useCallback(() => fetchDataRoomDocument(documentId), [documentId]);
  const { state, reload } = useAsync(load);

  const [editing, setEditing] = useState(false);
  const [editKey, setEditKey] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editNote, setEditNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<readonly DataRoomRevisionMeta[] | null>(null);
  const [viewing, setViewing] = useState<DataRoomRevisionDetail | null>(null);
  const [restoreTarget, setRestoreTarget] = useState<number | null>(null);
  const [restoreKey, setRestoreKey] = useState('');
  const [restoreBusy, setRestoreBusy] = useState(false);
  const [restoreFailure, setRestoreFailure] = useState<ApiFailure | null>(null);

  // Soft deletion and its undo, each a dialog with its own key per opening.
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteKey, setDeleteKey] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteFailure, setDeleteFailure] = useState<ApiFailure | null>(null);
  const [recoverOpen, setRecoverOpen] = useState(false);
  const [recoverKey, setRecoverKey] = useState('');
  const [recoverBusy, setRecoverBusy] = useState(false);
  const [recoverFailure, setRecoverFailure] = useState<ApiFailure | null>(null);

  const refreshAll = useCallback(() => {
    setHistory(null);
    setHistoryOpen(false);
    setViewing(null);
    reload();
  }, [reload]);

  const openHistory = useCallback(() => {
    setHistoryOpen(true);
    // Refetched on every opening — the history may have grown since.
    fetchDataRoomHistory(documentId).then(
      (result) => {
        setHistory(result.revisions);
      },
      (thrown: unknown) => {
        setHistoryOpen(false);
        setFailure(thrown instanceof ApiFailure ? thrown : new ApiFailure('failed'));
      },
    );
  }, [documentId]);

  if (session.status === 'authenticated' && !session.profile.mayUseDataRoom) {
    return <NotAvailablePage />;
  }
  if (state.status === 'loading') return <LoadingPanel />;
  if (state.status === 'failed') {
    if (state.failure.kind === 'forbidden' || state.failure.kind === 'missing') {
      return <NotAvailablePage />;
    }
    return <FailurePanel failure={state.failure} onRetry={reload} />;
  }

  const document = state.value.document;
  const folderPath = state.value.folderPath;
  const deleted = document.deletedAt !== null;

  const startEditing = (): void => {
    setEditing(true);
    setEditKey(newIdempotencyKey());
    setEditTitle(document.title);
    setEditContent(document.content);
    setEditNote('');
    setFailure(null);
    setViewing(null);
  };

  const save = (): void => {
    if (editContent.trim().length === 0) return;
    setBusy(true);
    setFailure(null);
    editDataRoomDocument(editKey, document.id, {
      expectedVersion: document.version,
      content: editContent,
      ...(editTitle.trim() !== document.title && editTitle.trim().length > 0
        ? { title: editTitle.trim() }
        : {}),
      ...(editNote.trim().length > 0 ? { changeNote: editNote.trim() } : {}),
    })
      .then((result) => {
        setBusy(false);
        setEditing(false);
        announce(
          result.changed
            ? copy.dataRoom.toastSaved(result.version)
            : copy.dataRoom.toastUnchanged,
        );
        refreshAll();
      })
      .catch((thrown: unknown) => {
        setBusy(false);
        setFailure(thrown instanceof ApiFailure ? thrown : new ApiFailure('failed'));
      });
  };

  const restore = (): void => {
    if (restoreTarget === null) return;
    setRestoreBusy(true);
    setRestoreFailure(null);
    restoreDataRoomRevision(restoreKey, document.id, {
      expectedVersion: document.version,
      version: restoreTarget,
    })
      .then((result) => {
        setRestoreBusy(false);
        setRestoreTarget(null);
        announce(
          result.changed
            ? copy.dataRoom.toastRestored(result.version)
            : copy.dataRoom.toastUnchanged,
        );
        refreshAll();
      })
      .catch((thrown: unknown) => {
        setRestoreBusy(false);
        setRestoreFailure(
          thrown instanceof ApiFailure ? thrown : new ApiFailure('failed'),
        );
      });
  };

  const removeDocument = (): void => {
    setDeleteBusy(true);
    setDeleteFailure(null);
    deleteDataRoomDocument(deleteKey, document.id, {
      expectedVersion: document.version,
    })
      .then(() => {
        setDeleteBusy(false);
        setDeleteOpen(false);
        announce(copy.dataRoom.toastDocumentDeleted);
        refreshAll();
      })
      .catch((thrown: unknown) => {
        setDeleteBusy(false);
        setDeleteFailure(
          thrown instanceof ApiFailure ? thrown : new ApiFailure('failed'),
        );
      });
  };

  const recover = (): void => {
    setRecoverBusy(true);
    setRecoverFailure(null);
    recoverDataRoomDocument(recoverKey, document.id, {
      expectedVersion: document.version,
    })
      .then(() => {
        setRecoverBusy(false);
        setRecoverOpen(false);
        announce(copy.dataRoom.toastDocumentRecovered);
        refreshAll();
      })
      .catch((thrown: unknown) => {
        setRecoverBusy(false);
        setRecoverFailure(
          thrown instanceof ApiFailure ? thrown : new ApiFailure('failed'),
        );
      });
  };

  const viewRevision = (version: number): void => {
    fetchDataRoomRevision(document.id, version).then(
      (result) => {
        setViewing(result.revision);
        // Reading happens in the document, not in the drawer: close it so
        // the content is what the person sees, with the banner as the way
        // back. Focus returns to the "Version history" button.
        setHistoryOpen(false);
      },
      (thrown: unknown) => {
        setFailure(thrown instanceof ApiFailure ? thrown : new ApiFailure('failed'));
      },
    );
  };

  const conflict = failure !== null && failure.kind === 'conflict';

  const dialogFailureAlert = (
    dialogFailure: ApiFailure | null,
  ): ReactNode | undefined =>
    dialogFailure === null ? undefined : (
      <InlineAlert
        tone="error"
        title={
          dialogFailure.kind === 'conflict'
            ? copy.states.conflictTitle
            : copy.states.saveFailedTitle
        }
        role="alert"
      >
        {dialogFailure.kind === 'conflict'
          ? copy.dataRoom.conflictBody
          : (dialogFailure.detail ?? copy.states.saveFailedBody)}
      </InlineAlert>
    );

  return (
    <>
      <nav
        aria-label={copy.dataRoom.breadcrumbLabel}
        style={{ marginBottom: 'var(--space-sm)' }}
      >
        <ol
          style={{
            listStyle: 'none',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
            padding: 0,
            margin: 0,
            font: 'var(--type-body-md)',
          }}
        >
          {[
            { to: '/data-room', label: copy.dataRoom.title },
            {
              to: `/data-room/category/${document.category}`,
              label: copy.dataRoom.categories[document.category] ?? document.category,
            },
            ...folderPath.map((folder) => ({
              to: `/data-room/folder/${folder.id}`,
              label: folder.name,
            })),
            { to: null as string | null, label: document.title },
          ].map((crumb, index) => (
            <li key={index} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {index > 0 ? (
                <span aria-hidden="true" style={{ color: 'var(--text-muted)' }}>
                  /
                </span>
              ) : null}
              {crumb.to === null ? (
                <span aria-current="page" style={{ color: 'var(--text-primary)' }}>
                  {crumb.label}
                </span>
              ) : (
                <Link to={crumb.to} style={{ color: 'var(--text-link)' }}>
                  {crumb.label}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </nav>

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 'var(--space-md)',
          flexWrap: 'wrap',
          marginBottom: 'var(--space-md)',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <h1
            style={{
              font: 'var(--type-display-md)',
              letterSpacing: 'var(--type-display-md-tracking)',
              color: 'var(--text-primary)',
              margin: '0 0 8px',
            }}
          >
            {document.title}
          </h1>
          <div
            style={{
              display: 'flex',
              gap: 12,
              alignItems: 'center',
              flexWrap: 'wrap',
              font: 'var(--type-caption)',
              color: 'var(--text-muted)',
            }}
          >
            <Tag tone="info">
              {copy.dataRoom.categories[document.category] ?? document.category}
            </Tag>
            {workspaceSlots().documentBadge?.({ audience: document.audience })}
            <span
              className="mj-ltr"
              dir="ltr"
              style={{ font: 'var(--type-body-tabular)' }}
            >
              {copy.dataRoom.currentVersion(document.version)}
            </span>
            <span>
              {copy.dataRoom.updatedByLabel}: {document.updatedBy.displayName}
            </span>
            <EventTime iso={document.updatedAt} />
          </div>
        </div>

        {editing ? null : (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {deleted ? null : (
              <Button variant="primary" onClick={startEditing}>
                {copy.dataRoom.edit}
              </Button>
            )}
            <Button variant="outline" aria-expanded={historyOpen} onClick={openHistory}>
              {copy.dataRoom.history}
            </Button>
            {deleted
              ? null
              : workspaceSlots().documentActions?.({ document, onChanged: refreshAll })}
            {deleted ? (
              <Button
                variant="primary"
                onClick={() => {
                  setRecoverOpen(true);
                  setRecoverKey(newIdempotencyKey());
                  setRecoverFailure(null);
                }}
              >
                {copy.dataRoom.recoverDocument}
              </Button>
            ) : (
              <Button
                variant="danger"
                onClick={() => {
                  setDeleteOpen(true);
                  setDeleteKey(newIdempotencyKey());
                  setDeleteFailure(null);
                }}
              >
                {copy.dataRoom.deleteDocument}
              </Button>
            )}
          </div>
        )}
      </div>

      {deleted ? (
        <InlineAlert
          tone="warning"
          title={copy.dataRoom.deletedBannerTitle}
          role="status"
        >
          <p style={{ margin: 0 }}>
            {copy.dataRoom.deletedBannerBody(
              document.deletedBy?.displayName ?? document.updatedBy.displayName,
            )}{' '}
            <EventTime iso={document.deletedAt} />
          </p>
        </InlineAlert>
      ) : null}

      {failure !== null && !editing ? (
        <InlineAlert
          tone="error"
          title={conflict ? copy.states.conflictTitle : copy.states.failedTitle}
          role="alert"
        >
          <p style={{ marginBottom: 10 }}>
            {conflict
              ? copy.dataRoom.conflictBody
              : (failure.detail ?? copy.states.failedBody)}
          </p>
          <Button size="sm" variant="outline" onClick={refreshAll}>
            {copy.states.reload}
          </Button>
        </InlineAlert>
      ) : null}

      {editing ? (
        <form
          style={{ maxWidth: 840 }}
          onSubmit={(event) => {
            event.preventDefault();
            save();
          }}
        >
          {failure !== null ? (
            <InlineAlert
              tone="error"
              title={conflict ? copy.states.conflictTitle : copy.states.saveFailedTitle}
              role="alert"
            >
              {conflict
                ? copy.dataRoom.conflictBody
                : (failure.detail ?? copy.states.saveFailedBody)}
            </InlineAlert>
          ) : null}

          <TextField
            label={copy.dataRoom.titleLabel}
            value={editTitle}
            onChange={setEditTitle}
            maxLength={DATA_ROOM_MAX_TITLE_LENGTH}
          />
          <MarkdownField
            label={copy.dataRoom.contentLabel}
            hint={copy.dataRoom.contentHint}
            value={editContent}
            onChange={setEditContent}
            rows={18}
            maxLength={DATA_ROOM_MAX_CONTENT_LENGTH}
            error={
              editContent.trim().length === 0 ? copy.dataRoom.contentMissing : null
            }
          />
          <TextField
            label={copy.dataRoom.changeNoteLabel}
            hint={copy.dataRoom.changeNoteHint}
            value={editNote}
            onChange={setEditNote}
            maxLength={500}
          />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button variant="primary" type="submit" busy={busy}>
              {busy ? copy.dataRoom.saving : copy.dataRoom.save}
            </Button>
            <Button
              variant="quiet"
              onClick={() => {
                setEditing(false);
                setFailure(null);
              }}
            >
              {copy.actions.cancel}
            </Button>
          </div>
        </form>
      ) : (
        <>
          {viewing !== null ? (
            <InlineAlert tone="info" role="status">
              <p style={{ marginBottom: 10 }}>
                {copy.dataRoom.viewingOld(viewing.version, document.version)}
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setViewing(null);
                }}
              >
                {copy.dataRoom.backToCurrent}
              </Button>
            </InlineAlert>
          ) : null}

          <div
            style={{
              background: 'var(--surface-card)',
              border: '1px solid var(--border-hairline)',
              borderRadius: 'var(--radius-card)',
              padding: 'var(--space-lg)',
              maxWidth: 840,
            }}
          >
            <MarkdownView
              source={viewing === null ? document.content : viewing.content}
              maxLength={DATA_ROOM_MAX_CONTENT_LENGTH}
            />
          </div>
        </>
      )}

      <Drawer
        open={historyOpen}
        title={copy.dataRoom.historyTitle}
        closeLabel={copy.actions.close}
        onClose={() => {
          setHistoryOpen(false);
        }}
      >
        {history === null ? (
          <p
            role="status"
            style={{ font: 'var(--type-body-md)', color: 'var(--text-muted)' }}
          >
            {copy.states.loading}
          </p>
        ) : (
          <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {history.map((revision) => (
              <li
                key={revision.version}
                style={{
                  padding: '12px 0',
                  borderBlockEnd: '1px solid var(--border-hairline)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    marginBottom: 4,
                  }}
                >
                  <span
                    className="mj-ltr"
                    dir="ltr"
                    style={{
                      font: 'var(--type-body-tabular)',
                      color: 'var(--text-primary)',
                    }}
                  >
                    {copy.dataRoom.versionLabel(revision.version)}
                  </span>
                  {revision.version === document.version ? (
                    <Tag tone="success">{copy.dataRoom.currentBadge}</Tag>
                  ) : null}
                  {revision.restoredFromVersion !== null ? (
                    <Tag tone="neutral" dashed>
                      {copy.dataRoom.restoredFrom(revision.restoredFromVersion)}
                    </Tag>
                  ) : null}
                </div>
                <div
                  style={{
                    font: 'var(--type-caption)',
                    color: 'var(--text-muted)',
                    display: 'flex',
                    gap: 8,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                  }}
                >
                  <span>{revision.createdBy.displayName}</span>
                  <EventTime iso={revision.createdAt} />
                </div>
                {revision.changeNote !== null ? (
                  <p
                    style={{
                      font: 'var(--type-body-md)',
                      color: 'var(--text-secondary)',
                      margin: '6px 0 0',
                    }}
                  >
                    {revision.changeNote}
                  </p>
                ) : null}
                {revision.version !== document.version ? (
                  <div
                    style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}
                  >
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        viewRevision(revision.version);
                      }}
                    >
                      {copy.dataRoom.view}
                    </Button>
                    {deleted ? null : (
                      <Button
                        size="sm"
                        variant="quiet"
                        onClick={() => {
                          // The drawer closes first; the confirm dialog is
                          // its own moment, and focus lands back on the
                          // history button when everything is done.
                          setHistoryOpen(false);
                          setRestoreTarget(revision.version);
                          setRestoreKey(newIdempotencyKey());
                          setRestoreFailure(null);
                        }}
                      >
                        {copy.dataRoom.restore}
                      </Button>
                    )}
                  </div>
                ) : null}
              </li>
            ))}
          </ol>
        )}
      </Drawer>

      <ConfirmDialog
        open={restoreTarget !== null}
        title={copy.dataRoom.restoreTitle}
        body={restoreTarget === null ? '' : copy.dataRoom.restoreBody(restoreTarget)}
        whatHappensLabel={copy.dialogs.whatHappens}
        effects={copy.dataRoom.restoreEffects}
        confirmLabel={copy.dataRoom.restore}
        cancelLabel={copy.actions.cancel}
        busy={restoreBusy}
        error={dialogFailureAlert(restoreFailure)}
        onConfirm={restore}
        onCancel={() => {
          if (!restoreBusy) setRestoreTarget(null);
        }}
      />

      <ConfirmDialog
        open={deleteOpen}
        title={copy.dataRoom.deleteDocumentTitle}
        body={copy.dataRoom.deleteDocumentBody(document.title)}
        whatHappensLabel={copy.dialogs.whatHappens}
        effects={copy.dataRoom.deleteDocumentEffects}
        confirmLabel={copy.dataRoom.deleteDocument}
        cancelLabel={copy.actions.cancel}
        busy={deleteBusy}
        danger
        error={dialogFailureAlert(deleteFailure)}
        onConfirm={removeDocument}
        onCancel={() => {
          if (!deleteBusy) setDeleteOpen(false);
        }}
      />

      <ConfirmDialog
        open={recoverOpen}
        title={copy.dataRoom.recoverTitle}
        body={copy.dataRoom.recoverBody(document.title)}
        whatHappensLabel={copy.dialogs.whatHappens}
        effects={copy.dataRoom.recoverEffects}
        confirmLabel={copy.dataRoom.recoverDocument}
        cancelLabel={copy.actions.cancel}
        busy={recoverBusy}
        error={dialogFailureAlert(recoverFailure)}
        onConfirm={recover}
        onCancel={() => {
          if (!recoverBusy) setRecoverOpen(false);
        }}
      />
    </>
  );
}
